import { useMutation, useQueryClient } from '@tanstack/react-query';

import { axiosPrivate } from 'app/utils';

import { markWebsiteAsChanged } from '../../../../hooks/usePlusWebsites';
import type { PageDetailsResponse } from './useGetPageDetails';
import { pageDetailsQueryKey } from './useGetPageDetails';

interface DeleteSectionParams {
  sectionId: string;
}

function deleteSection(websiteId: string, pageId: string, { sectionId }: DeleteSectionParams) {
  return axiosPrivate
    .delete(`/v3/pharmacist/plus-websites/${websiteId}/pages/${pageId}/sections/${sectionId}`)
    .then((r) => r.data);
}

export function useDeleteSection({ websiteId, pageId }: { websiteId: string; pageId: string }) {
  const queryClient = useQueryClient();
  const queryKey = pageDetailsQueryKey(websiteId, pageId);

  return useMutation({
    mutationFn: (params: DeleteSectionParams) => deleteSection(websiteId, pageId, params),
    onMutate: async ({ sectionId }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PageDetailsResponse>(queryKey);

      if (previous) {
        const sections = previous.sections.filter((section) => section.id !== sectionId);
        queryClient.setQueryData<PageDetailsResponse>(queryKey, { ...previous, sections });
      }

      return { previous };
    },
    onError: (_error, _params, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSuccess: () => markWebsiteAsChanged(queryClient, websiteId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    meta: {
      successMessage: 'Section deleted successfully',
      errorMessage: 'Failed to delete section',
    },
  });
}
